
import { useState, useEffect } from "react";
import {
  X, CheckCircle, AlertCircle, FileText, Loader2, Download,
  RefreshCw, ExternalLink, Briefcase,
} from "lucide-react";

// ─── Avatar colour pool ───────────────────────────────────────────────────────
const AV_COLORS = [
  { bg: "#dbeafe", color: "#1d4ed8" },
  { bg: "#dcfce7", color: "#166534" },
  { bg: "#ede9fe", color: "#5b21b6" },
  { bg: "#fff7ed", color: "#c2410c" },
  { bg: "#fce7f3", color: "#9d174d" },
  { bg: "#f0fdf4", color: "#14532d" },
];
export const avColor = (name) => AV_COLORS[(name || "A").charCodeAt(0) % AV_COLORS.length];
export const initials = (name) =>
  (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

// ─── Resume Preview Panel ─────────────────────────────────────────────────────
// Strategy: S3 URLs often have Content-Disposition: attachment (triggers download)
// and X-Frame-Options blocking direct iframe embeds.
// Fix: Route through Google Docs Viewer which fetches the file server-side
// and renders it safely — no download, no CORS issue.
const GDOCS_VIEWER = (url) =>
  `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;

const ResumePreviewPanel = ({ candidate, onClose }) => {
  const [viewerState, setViewerState] = useState("loading"); // "loading" | "ready" | "error"
  const [retryKey, setRetryKey] = useState(0);
  const av = avColor(candidate?.name);

  // Reset state whenever the candidate changes
  useEffect(() => {
    setViewerState("loading");
    setRetryKey((k) => k + 1);
  }, [candidate?.id]);

  // Google Docs Viewer sometimes fires onLoad even on error pages,
  // so we use a 30s timeout — if it hasn't confirmed ready, show error UI.
  useEffect(() => {
    if (viewerState !== "loading") return;
    const t = setTimeout(() => {
      setViewerState("error");
    }, 30000);
    return () => clearTimeout(t);
  }, [viewerState, retryKey]);

  if (!candidate) return null;

  // Google Docs Viewer is unreliable for .doc/.docx (and many S3 URLs without
  // a real extension). Detect those and skip straight to the open/download card.
  const urlLower = (candidate.resumeUrl || "").toLowerCase();
 const isUnsupportedForInlinePreview = urlLower.endsWith(".txt");

  const viewerUrl =
    candidate.resumeUrl && !isUnsupportedForInlinePreview
      ? GDOCS_VIEWER(candidate.resumeUrl)
      : null;

  return (
    <>
      {/* Backdrop — mobile only */}
      <div
        className="fixed inset-0 bg-black/30 z-40 md:hidden"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full z-50 w-full md:w-[520px] lg:w-[600px] bg-white shadow-2xl border-l border-gray-200 flex flex-col"
        style={{ animation: "slideInRight 0.28s cubic-bezier(0.16,1,0.3,1)" }}
      >
        {/* ── Header ── */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-white flex-shrink-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
            style={{ background: av.bg, color: av.color }}
          >
            {initials(candidate.name)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">
              {candidate.name && candidate.name !== "Unknown" ? candidate.name : "Unknown Candidate"}
            </p>
            <div className="flex items-center gap-3">
              {candidate.experience != null && (
                <span className="text-[11px] text-gray-400 flex items-center gap-1">
                  <Briefcase className="w-2.5 h-2.5" />
                  {candidate.experience} yr{candidate.experience !== 1 ? "s" : ""}
                </span>
              )}
              <span className="text-[11px] text-gray-400">
                {new Date(candidate.createdAt).toLocaleDateString("en-IN", {
                  day: "numeric", month: "short", year: "numeric",
                })}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {candidate.resumeUrl && (
              <a
                href={candidate.resumeUrl}
                download
                title="Download resume"
                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#012760] hover:border-[#012760] transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
              </a>
            )}
            <button
              onClick={onClose}
              title="Close preview"
              className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* ── Skills strip ── */}
        {(candidate.skills || []).length > 0 && (
          <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50 flex gap-1.5 flex-wrap flex-shrink-0">
            {(candidate.skills || []).slice(0, 8).map((s) => (
              <span
                key={s}
                className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
              >
                {s}
              </span>
            ))}
            {(candidate.skills || []).length > 8 && (
              <span className="text-[11px] text-gray-400 px-1 py-0.5">
                +{(candidate.skills || []).length - 8} more
              </span>
            )}
          </div>
        )}

        {/* ── Viewer area ── */}
        <div className="flex-1 relative overflow-hidden bg-gray-100">
          {viewerUrl ? (
            <>
              {/* Loading overlay */}
              {viewerState === "loading" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 gap-3">
                  <Loader2 className="w-8 h-8 text-[#012760] animate-spin" />
                  <p className="text-sm text-gray-400 font-medium">Loading resume…</p>
                  <p className="text-xs text-gray-300">This may take a few seconds</p>
                </div>
              )}

              {/* Error / fallback state */}
              {viewerState === "error" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 px-8 text-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Preview unavailable</p>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      The resume couldn't be rendered inline. Use the buttons below to view or download it.
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-center">
                    <button
                      onClick={() => {
                        setViewerState("loading");
                        setRetryKey((k) => k + 1);
                      }}
                      className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 text-xs font-semibold rounded-xl hover:border-gray-300 transition-colors"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Retry
                    </button>
                    <a
                      href={candidate.resumeUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#012760] text-white text-xs font-semibold rounded-xl hover:bg-[#023080] transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Open in New Tab
                    </a>
                    <a
                      href={candidate.resumeUrl}
                      download
                      className="flex items-center gap-1.5 px-4 py-2 border border-[#012760] text-[#012760] text-xs font-semibold rounded-xl hover:bg-[#012760]/5 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /> Download
                    </a>
                  </div>
                </div>
              )}

              {/* Google Docs Viewer iframe — never triggers download */}
              <iframe
                key={`${candidate.id}-${retryKey}`}
                src={viewerUrl}
                className="w-full h-full border-0"
                title={`Resume — ${candidate.name}`}
                onLoad={() => setViewerState("ready")}
                onError={() => setViewerState("error")}
                style={{ opacity: viewerState === "ready" ? 1 : 0 }}
              />
            </>
          ) : candidate.resumeUrl ? (
            // Unsupported inline preview type (.doc/.docx/.txt) — render a
            // resume-style document generated from the parsed JSON we already
            // have in the DB, with the original file available as a fallback.
            <div className="h-full overflow-y-auto bg-gray-100 p-4 md:p-6">
              <div className="max-w-2xl mx-auto bg-white rounded-sm shadow-md border border-gray-200 p-8 md:p-10">
                {/* Header */}
                <div className="border-b border-gray-200 pb-4 mb-5">
                  <h2 className="text-xl font-bold text-gray-800">
                    {candidate.name && candidate.name !== "Unknown" ? candidate.name : "Unknown Candidate"}
                  </h2>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                    {candidate.email && <span>{candidate.email}</span>}
                    {candidate.phone && <span>{candidate.phone}</span>}
                    {candidate.experience != null && (
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {candidate.experience} yr{candidate.experience !== 1 ? "s" : ""} experience
                      </span>
                    )}
                  </div>
                </div>

                {/* Skills */}
                {(candidate.skills || []).length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Skills</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(candidate.skills || []).map((s) => (
                        <span
                          key={s}
                          className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Any other parsed fields, shown generically */}
                {Object.entries(candidate)
                  .filter(([key, value]) =>
                    !["id", "name", "email", "phone", "skills", "experience", "resumeUrl", "createdAt", "_id", "__v"].includes(key) &&
                    value !== null && value !== undefined && value !== "" &&
                    !(Array.isArray(value) && value.length === 0)
                  )
                  .map(([key, value]) => (
                    <div key={key} className="mb-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                        {key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}
                      </p>
                      {Array.isArray(value) ? (
                        <div className="flex flex-wrap gap-1.5">
                          {value.map((v, i) => (
                            <span
                              key={i}
                              className="bg-gray-50 border border-gray-200 text-gray-600 text-[11px] font-medium px-2 py-0.5 rounded-md"
                            >
                              {typeof v === "object" ? JSON.stringify(v) : String(v)}
                            </span>
                          ))}
                        </div>
                      ) : typeof value === "object" ? (
                        <pre className="text-[11px] text-gray-500 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      ) : (
                        <p className="text-sm text-gray-700">{String(value)}</p>
                      )}
                    </div>
                  ))}

                {/* Uploaded date */}
                <p className="text-[11px] text-gray-300 mt-6 pt-4 border-t border-gray-100">
                  Uploaded {new Date(candidate.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              </div>

              {/* Original file actions */}
              <div className="max-w-2xl mx-auto mt-3 flex gap-2 justify-end">
                <a
                  href={candidate.resumeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-500 text-[11px] font-semibold rounded-lg hover:border-gray-300 transition-colors bg-white"
                >
                  <ExternalLink className="w-3 h-3" /> Open Original File
                </a>
                <a
                  href={candidate.resumeUrl}
                  download
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-500 text-[11px] font-semibold rounded-lg hover:border-gray-300 transition-colors bg-white"
                >
                  <Download className="w-3 h-3" /> Download Original
                </a>
              </div>
            </div>
          ) : (
            // No resumeUrl — show parsed info fallback
            <div className="h-full overflow-y-auto p-6">
              <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center mb-4">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-sm font-semibold text-gray-700 mb-1">No file preview available</p>
                <p className="text-xs text-gray-400">Resume file URL was not stored.</p>
              </div>
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Parsed Information</p>
                  <div className="space-y-2">
                    {[
                      { label: "Name", value: candidate.name },
                      { label: "Experience", value: candidate.experience != null ? `${candidate.experience} years` : null },
                      { label: "Email", value: candidate.email },
                      { label: "Phone", value: candidate.phone },
                    ].filter(f => f.value).map(({ label, value }) => (
                      <div key={label} className="flex items-start gap-3">
                        <span className="text-xs text-gray-400 w-20 flex-shrink-0">{label}</span>
                        <span className="text-xs font-medium text-gray-700">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {(candidate.skills || []).length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-100 p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">All Skills</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(candidate.skills || []).map((s) => (
                        <span key={s} className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0.6; }
          to   { transform: translateX(0);    opacity: 1;   }
        }
      `}</style>
    </>
  );
};

export default ResumePreviewPanel;








// import { useState, useEffect } from "react";
// import {
//   X,
//   AlertCircle,
//   FileText,
//   Loader2,
//   Download,
//   RefreshCw,
//   ExternalLink,
//   Briefcase,
// } from "lucide-react";

// const AV_COLORS = [
//   { bg: "#dbeafe", color: "#1d4ed8" },
//   { bg: "#dcfce7", color: "#166534" },
//   { bg: "#ede9fe", color: "#5b21b6" },
//   { bg: "#fff7ed", color: "#c2410c" },
//   { bg: "#fce7f3", color: "#9d174d" },
//   { bg: "#f0fdf4", color: "#14532d" },
// ];

// export const avColor = (name) =>
//   AV_COLORS[(name || "A").charCodeAt(0) % AV_COLORS.length];

// export const initials = (name) =>
//   (name || "?")
//     .split(" ")
//     .map((w) => w[0])
//     .join("")
//     .slice(0, 2)
//     .toUpperCase();

// const GDOCS_VIEWER = (url) =>
//   `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;

// const EXCLUDED_KEYS = [
//   "id", "_id", "__v",
//   "name", "email", "phone", "location", "visaType",
//   "skills", "experience", "resumeUrl", "createdAt",
// ];

// const PinIcon = () => (
//   <svg
//     className="w-3 h-3 text-gray-400 flex-shrink-0"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     viewBox="0 0 24 24"
//   >
//     <path
//       strokeLinecap="round"
//       strokeLinejoin="round"
//       d="M12 2C8.686 2 6 4.686 6 8c0 5.25 6 13 6 13s6-7.75 6-13c0-3.314-2.686-6-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"
//     />
//   </svg>
// );

// const MailIcon = () => (
//   <svg
//     className="w-3 h-3 flex-shrink-0"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     viewBox="0 0 24 24"
//   >
//     <path
//       strokeLinecap="round"
//       strokeLinejoin="round"
//       d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
//     />
//   </svg>
// );

// const PhoneIcon = () => (
//   <svg
//     className="w-3 h-3 flex-shrink-0"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     viewBox="0 0 24 24"
//   >
//     <path
//       strokeLinecap="round"
//       strokeLinejoin="round"
//       d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
//     />
//   </svg>
// );

// const VisaIcon = () => (
//   <svg
//     className="w-3 h-3 flex-shrink-0"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     viewBox="0 0 24 24"
//   >
//     <path
//       strokeLinecap="round"
//       strokeLinejoin="round"
//       d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0"
//     />
//   </svg>
// );

// const ResumePreviewPanel = ({ candidate, onClose }) => {
//   const [viewerState, setViewerState] = useState("loading");
//   const [retryKey, setRetryKey] = useState(0);
//   const av = avColor(candidate?.name);

//   useEffect(() => {
//     setViewerState("loading");
//     setRetryKey((k) => k + 1);
//   }, [candidate?.id]);

//   useEffect(() => {
//     if (viewerState !== "loading") return;
//     const t = setTimeout(() => setViewerState("error"), 30000);
//     return () => clearTimeout(t);
//   }, [viewerState, retryKey]);

//   if (!candidate) return null;

//   const urlLower = (candidate.resumeUrl || "").toLowerCase();
//   const isUnsupported = urlLower.endsWith(".txt");
//   const viewerUrl =
//     candidate.resumeUrl && !isUnsupported
//       ? GDOCS_VIEWER(candidate.resumeUrl)
//       : null;

//   const hasInfo =
//     !!candidate.location ||
//     !!candidate.email ||
//     !!candidate.phone ||
//     (!!candidate.visaType && candidate.visaType !== "Not Specified");

//   return (
//     <>
//       <div
//         className="fixed inset-0 bg-black/30 z-40 md:hidden"
//         onClick={onClose}
//       />

//       <div
//         className="fixed top-0 right-0 h-full z-50 w-full md:w-[520px] lg:w-[600px] bg-white shadow-2xl border-l border-gray-200 flex flex-col"
//         style={{ animation: "slideInRight 0.28s cubic-bezier(0.16,1,0.3,1)" }}
//       >

//         {/* Header */}
//         <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-white flex-shrink-0">
//           <div
//             className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
//             style={{ background: av.bg, color: av.color }}
//           >
//             {initials(candidate.name)}
//           </div>

//           <div className="flex-1 min-w-0">
//             <p className="text-sm font-semibold text-gray-800 truncate">
//               {candidate.name && candidate.name !== "Unknown"
//                 ? candidate.name
//                 : "Unknown Candidate"}
//             </p>
//             <div className="flex items-center gap-3">
//               {candidate.experience != null && (
//                 <span className="text-[11px] text-gray-400 flex items-center gap-1">
//                   <Briefcase className="w-2.5 h-2.5" />
//                   {candidate.experience} yr{candidate.experience !== 1 ? "s" : ""}
//                 </span>
//               )}
//               <span className="text-[11px] text-gray-400">
//                 {new Date(candidate.createdAt).toLocaleDateString("en-IN", {
//                   day: "numeric",
//                   month: "short",
//                   year: "numeric",
//                 })}
//               </span>
//             </div>
//           </div>

//           <div className="flex items-center gap-2 flex-shrink-0">
//             {candidate.resumeUrl && (
//               <a
//                 href={candidate.resumeUrl}
//                 download
//                 title="Download resume"
//                 className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#012760] hover:border-[#012760] transition-colors"
//               >
//                 <Download className="w-3.5 h-3.5" />
//               </a>
//             )}
//             <button
//               onClick={onClose}
//               title="Close preview"
//               className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
//             >
//               <X className="w-3.5 h-3.5" />
//             </button>
//           </div>
//         </div>

//         {/* Info strip */}
//         {hasInfo && (
//           <div className="px-5 py-2.5 border-b border-gray-100 bg-white flex items-center gap-4 flex-wrap flex-shrink-0">
//             {candidate.location && (
//               <span className="flex items-center gap-1 text-[11px] text-gray-500">
//                 <PinIcon />
//                 {candidate.location}
//               </span>
//             )}
//             {candidate.email && (
//               <a
//                 href={`mailto:${candidate.email}`}
//                 className="flex items-center gap-1 text-[11px] text-[#012760] hover:underline"
//               >
//                 <MailIcon />
//                 {candidate.email}
//               </a>
//             )}
//             {candidate.phone && (
//               <a
//                 href={`tel:${candidate.phone}`}
//                 className="flex items-center gap-1 text-[11px] text-[#012760] hover:underline"
//               >
//                 <PhoneIcon />
//                 {candidate.phone}
//               </a>
//             )}
//             {candidate.visaType && candidate.visaType !== "Not Specified" && (
//               <span className="flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-50 border border-purple-100 text-purple-700">
//                 <VisaIcon />
//                 {candidate.visaType}
//               </span>
//             )}
//           </div>
//         )}

//         {/* Skills strip */}
//         {(candidate.skills || []).length > 0 && (
//           <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50 flex gap-1.5 flex-wrap flex-shrink-0">
//             {(candidate.skills || []).slice(0, 8).map((s) => (
//               <span
//                 key={s}
//                 className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
//               >
//                 {s}
//               </span>
//             ))}
//             {(candidate.skills || []).length > 8 && (
//               <span className="text-[11px] text-gray-400 px-1 py-0.5">
//                 +{(candidate.skills || []).length - 8} more
//               </span>
//             )}
//           </div>
//         )}

//         {/* Viewer area */}
//         <div className="flex-1 relative overflow-hidden bg-gray-100">

//           {viewerUrl ? (
//             <>
//               {viewerState === "loading" && (
//                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 gap-3">
//                   <Loader2 className="w-8 h-8 text-[#012760] animate-spin" />
//                   <p className="text-sm text-gray-400 font-medium">Loading resume…</p>
//                   <p className="text-xs text-gray-300">This may take a few seconds</p>
//                 </div>
//               )}

//               {viewerState === "error" && (
//                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 px-8 text-center gap-4">
//                   <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center">
//                     <AlertCircle className="w-5 h-5 text-orange-400" />
//                   </div>
//                   <div>
//                     <p className="text-sm font-semibold text-gray-700 mb-1">
//                       Preview unavailable
//                     </p>
//                     <p className="text-xs text-gray-400 leading-relaxed">
//                       The resume could not be rendered inline. Use the buttons below to view or download it.
//                     </p>
//                   </div>
//                   <div className="flex gap-2 flex-wrap justify-center">
//                     <button
//                       onClick={() => {
//                         setViewerState("loading");
//                         setRetryKey((k) => k + 1);
//                       }}
//                       className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 text-xs font-semibold rounded-xl hover:border-gray-300 transition-colors"
//                     >
//                       <RefreshCw className="w-3.5 h-3.5" />
//                       Retry
//                     </button>
//                     <a
//                       href={candidate.resumeUrl}
//                       target="_blank"
//                       rel="noreferrer"
//                       className="flex items-center gap-1.5 px-4 py-2 bg-[#012760] text-white text-xs font-semibold rounded-xl hover:bg-[#023080] transition-colors"
//                     >
//                       <ExternalLink className="w-3.5 h-3.5" />
//                       Open in New Tab
//                     </a>
//                     <a
//                       href={candidate.resumeUrl}
//                       download
//                       className="flex items-center gap-1.5 px-4 py-2 border border-[#012760] text-[#012760] text-xs font-semibold rounded-xl hover:bg-[#012760]/5 transition-colors"
//                     >
//                       <Download className="w-3.5 h-3.5" />
//                       Download
//                     </a>
//                   </div>
//                 </div>
//               )}

//               <iframe
//                 key={`${candidate.id}-${retryKey}`}
//                 src={viewerUrl}
//                 className="w-full h-full border-0"
//                 title={`Resume - ${candidate.name}`}
//                 onLoad={() => setViewerState("ready")}
//                 onError={() => setViewerState("error")}
//                 style={{ opacity: viewerState === "ready" ? 1 : 0 }}
//               />
//             </>

//           ) : candidate.resumeUrl ? (
//             <div className="h-full overflow-y-auto bg-gray-100 p-4 md:p-6">
//               <div className="max-w-2xl mx-auto bg-white rounded-sm shadow-md border border-gray-200 p-8 md:p-10">

//                 <div className="border-b border-gray-200 pb-4 mb-5">
//                   <h2 className="text-xl font-bold text-gray-800">
//                     {candidate.name && candidate.name !== "Unknown"
//                       ? candidate.name
//                       : "Unknown Candidate"}
//                   </h2>
//                   <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
//                     {candidate.email && (
//                       <a
//                         href={`mailto:${candidate.email}`}
//                         className="hover:text-[#012760] hover:underline"
//                       >
//                         {candidate.email}
//                       </a>
//                     )}
//                     {candidate.phone && (
//                       <a
//                         href={`tel:${candidate.phone}`}
//                         className="hover:text-[#012760] hover:underline"
//                       >
//                         {candidate.phone}
//                       </a>
//                     )}
//                     {candidate.location && (
//                       <span>{candidate.location}</span>
//                     )}
//                     {candidate.visaType && candidate.visaType !== "Not Specified" && (
//                       <span className="text-purple-600 font-medium">
//                         {candidate.visaType}
//                       </span>
//                     )}
//                     {candidate.experience != null && (
//                       <span className="flex items-center gap-1">
//                         <Briefcase className="w-3 h-3" />
//                         {candidate.experience} yr{candidate.experience !== 1 ? "s" : ""} experience
//                       </span>
//                     )}
//                   </div>
//                 </div>

//                 {(candidate.skills || []).length > 0 && (
//                   <div className="mb-5">
//                     <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
//                       Skills
//                     </p>
//                     <div className="flex flex-wrap gap-1.5">
//                       {(candidate.skills || []).map((s) => (
//                         <span
//                           key={s}
//                           className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
//                         >
//                           {s}
//                         </span>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {Object.entries(candidate)
//                   .filter(([key, value]) =>
//                     !EXCLUDED_KEYS.includes(key) &&
//                     value !== null &&
//                     value !== undefined &&
//                     value !== "" &&
//                     !(Array.isArray(value) && value.length === 0)
//                   )
//                   .map(([key, value]) => (
//                     <div key={key} className="mb-4">
//                       <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
//                         {key
//                           .replace(/([A-Z])/g, " $1")
//                           .replace(/^./, (c) => c.toUpperCase())}
//                       </p>
//                       {Array.isArray(value) ? (
//                         <div className="flex flex-wrap gap-1.5">
//                           {value.map((v, i) => (
//                             <span
//                               key={i}
//                               className="bg-gray-50 border border-gray-200 text-gray-600 text-[11px] font-medium px-2 py-0.5 rounded-md"
//                             >
//                               {typeof v === "object" ? JSON.stringify(v) : String(v)}
//                             </span>
//                           ))}
//                         </div>
//                       ) : typeof value === "object" ? (
//                         <pre className="text-[11px] text-gray-500 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
//                           {JSON.stringify(value, null, 2)}
//                         </pre>
//                       ) : (
//                         <p className="text-sm text-gray-700">{String(value)}</p>
//                       )}
//                     </div>
//                   ))}

//                 <p className="text-[11px] text-gray-300 mt-6 pt-4 border-t border-gray-100">
//                   Uploaded{" "}
//                   {new Date(candidate.createdAt).toLocaleDateString("en-IN", {
//                     day: "numeric",
//                     month: "short",
//                     year: "numeric",
//                   })}
//                 </p>
//               </div>

//               <div className="max-w-2xl mx-auto mt-3 flex gap-2 justify-end">
//                 <a
//                   href={candidate.resumeUrl}
//                   target="_blank"
//                   rel="noreferrer"
//                   className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-500 text-[11px] font-semibold rounded-lg hover:border-gray-300 transition-colors bg-white"
//                 >
//                   <ExternalLink className="w-3 h-3" />
//                   Open Original File
//                 </a>
//                 <a
//                   href={candidate.resumeUrl}
//                   download
//                   className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-500 text-[11px] font-semibold rounded-lg hover:border-gray-300 transition-colors bg-white"
//                 >
//                   <Download className="w-3 h-3" />
//                   Download Original
//                 </a>
//               </div>
//             </div>

//           ) : (
//             <div className="h-full overflow-y-auto p-6">
//               <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center mb-4">
//                 <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
//                   <FileText className="w-5 h-5 text-gray-400" />
//                 </div>
//                 <p className="text-sm font-semibold text-gray-700 mb-1">
//                   No file preview available
//                 </p>
//                 <p className="text-xs text-gray-400">Resume file URL was not stored.</p>
//               </div>

//               <div className="space-y-4">
//                 <div className="bg-white rounded-xl border border-gray-100 p-4">
//                   <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
//                     Parsed Information
//                   </p>
//                   <div className="space-y-2">
//                     {[
//                       { label: "Name", value: candidate.name },
//                       { label: "Email", value: candidate.email },
//                       { label: "Phone", value: candidate.phone },
//                       { label: "Location", value: candidate.location },
//                       {
//                         label: "Visa Type",
//                         value:
//                           candidate.visaType && candidate.visaType !== "Not Specified"
//                             ? candidate.visaType
//                             : null,
//                       },
//                       {
//                         label: "Experience",
//                         value:
//                           candidate.experience != null
//                             ? `${candidate.experience} years`
//                             : null,
//                       },
//                     ]
//                       .filter((f) => f.value)
//                       .map(({ label, value }) => (
//                         <div key={label} className="flex items-start gap-3">
//                           <span className="text-xs text-gray-400 w-20 flex-shrink-0">
//                             {label}
//                           </span>
//                           <span className="text-xs font-medium text-gray-700">
//                             {value}
//                           </span>
//                         </div>
//                       ))}
//                   </div>
//                 </div>

//                 {(candidate.skills || []).length > 0 && (
//                   <div className="bg-white rounded-xl border border-gray-100 p-4">
//                     <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
//                       All Skills
//                     </p>
//                     <div className="flex flex-wrap gap-1.5">
//                       {(candidate.skills || []).map((s) => (
//                         <span
//                           key={s}
//                           className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
//                         >
//                           {s}
//                         </span>
//                       ))}
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}

//         </div>
//       </div>

//       <style>{`
//         @keyframes slideInRight {
//           from { transform: translateX(100%); opacity: 0.6; }
//           to   { transform: translateX(0);    opacity: 1;   }
//         }
//       `}</style>
//     </>
//   );
// };

// export default ResumePreviewPanel;









// import { useState, useEffect } from "react";
// import {
//   X, CheckCircle, AlertCircle, FileText, Loader2, Download,
//   RefreshCw, ExternalLink, Briefcase,
// } from "lucide-react";

// // ─── Avatar colour pool ───────────────────────────────────────────────────────
// const AV_COLORS = [
//   { bg: "#dbeafe", color: "#1d4ed8" },
//   { bg: "#dcfce7", color: "#166534" },
//   { bg: "#ede9fe", color: "#5b21b6" },
//   { bg: "#fff7ed", color: "#c2410c" },
//   { bg: "#fce7f3", color: "#9d174d" },
//   { bg: "#f0fdf4", color: "#14532d" },
// ];
// export const avColor = (name) => AV_COLORS[(name || "A").charCodeAt(0) % AV_COLORS.length];
// export const initials = (name) =>
//   (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

// // ─── Resume Preview Panel ─────────────────────────────────────────────────────
// // Strategy: S3 URLs often have Content-Disposition: attachment (triggers download)
// // and X-Frame-Options blocking direct iframe embeds.
// // Fix: Route through Google Docs Viewer which fetches the file server-side
// // and renders it safely — no download, no CORS issue.
// const GDOCS_VIEWER = (url) =>
//   `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;

// const ResumePreviewPanel = ({ candidate, onClose }) => {
//   const [viewerState, setViewerState] = useState("loading"); // "loading" | "ready" | "error"
//   const [retryKey, setRetryKey] = useState(0);
//   const av = avColor(candidate?.name);

//   // Reset state whenever the candidate changes
//   useEffect(() => {
//     setViewerState("loading");
//     setRetryKey((k) => k + 1);
//   }, [candidate?.id]);

//   // Google Docs Viewer sometimes fires onLoad even on error pages,
//   // so we use a 30s timeout — if it hasn't confirmed ready, show error UI.
//   useEffect(() => {
//     if (viewerState !== "loading") return;
//     const t = setTimeout(() => {
//       setViewerState("error");
//     }, 30000);
//     return () => clearTimeout(t);
//   }, [viewerState, retryKey]);

//   if (!candidate) return null;

//   // Google Docs Viewer is unreliable for .doc/.docx (and many S3 URLs without
//   // a real extension). Detect those and skip straight to the open/download card.
//   const urlLower = (candidate.resumeUrl || "").toLowerCase();
//  const isUnsupportedForInlinePreview = urlLower.endsWith(".txt");

//   const viewerUrl =
//     candidate.resumeUrl && !isUnsupportedForInlinePreview
//       ? GDOCS_VIEWER(candidate.resumeUrl)
//       : null;

//   return (
//     <>
//       {/* Backdrop — mobile only */}
//       <div
//         className="fixed inset-0 bg-black/30 z-40 md:hidden"
//         onClick={onClose}
//       />

//       {/* Panel */}
//       <div
//         className="fixed top-0 right-0 h-full z-50 w-full md:w-[520px] lg:w-[600px] bg-white shadow-2xl border-l border-gray-200 flex flex-col"
//         style={{ animation: "slideInRight 0.28s cubic-bezier(0.16,1,0.3,1)" }}
//       >
//         {/* ── Header ── */}
//         <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-white flex-shrink-0">
//           <div
//             className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
//             style={{ background: av.bg, color: av.color }}
//           >
//             {initials(candidate.name)}
//           </div>
//           <div className="flex-1 min-w-0">
//             <p className="text-sm font-semibold text-gray-800 truncate">
//               {candidate.name && candidate.name !== "Unknown" ? candidate.name : "Unknown Candidate"}
//             </p>
//             <div className="flex items-center gap-3">
//               {candidate.experience != null && (
//                 <span className="text-[11px] text-gray-400 flex items-center gap-1">
//                   <Briefcase className="w-2.5 h-2.5" />
//                   {candidate.experience} yr{candidate.experience !== 1 ? "s" : ""}
//                 </span>
//               )}
//               <span className="text-[11px] text-gray-400">
//                 {new Date(candidate.createdAt).toLocaleDateString("en-IN", {
//                   day: "numeric", month: "short", year: "numeric",
//                 })}
//               </span>
//             </div>
//           </div>
//           <div className="flex items-center gap-2 flex-shrink-0">
//             {candidate.resumeUrl && (
//               <a
//                 href={candidate.resumeUrl}
//                 download
//                 title="Download resume"
//                 className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#012760] hover:border-[#012760] transition-colors"
//               >
//                 <Download className="w-3.5 h-3.5" />
//               </a>
//             )}
//             <button
//               onClick={onClose}
//               title="Close preview"
//               className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
//             >
//               <X className="w-3.5 h-3.5" />
//             </button>
//           </div>
//         </div>

//         {/* ── Skills strip ── */}
//         {(candidate.skills || []).length > 0 && (
//           <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50 flex gap-1.5 flex-wrap flex-shrink-0">
//             {(candidate.skills || []).slice(0, 8).map((s) => (
//               <span
//                 key={s}
//                 className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
//               >
//                 {s}
//               </span>
//             ))}
//             {(candidate.skills || []).length > 8 && (
//               <span className="text-[11px] text-gray-400 px-1 py-0.5">
//                 +{(candidate.skills || []).length - 8} more
//               </span>
//             )}
//           </div>
//         )}

//         {/* ── Viewer area ── */}
//         <div className="flex-1 relative overflow-hidden bg-gray-100">
//           {viewerUrl ? (
//             <>
//               {/* Loading overlay */}
//               {viewerState === "loading" && (
//                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 gap-3">
//                   <Loader2 className="w-8 h-8 text-[#012760] animate-spin" />
//                   <p className="text-sm text-gray-400 font-medium">Loading resume…</p>
//                   <p className="text-xs text-gray-300">This may take a few seconds</p>
//                 </div>
//               )}

//               {/* Error / fallback state */}
//               {viewerState === "error" && (
//                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 px-8 text-center gap-4">
//                   <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center">
//                     <AlertCircle className="w-5 h-5 text-orange-400" />
//                   </div>
//                   <div>
//                     <p className="text-sm font-semibold text-gray-700 mb-1">Preview unavailable</p>
//                     <p className="text-xs text-gray-400 leading-relaxed">
//                       The resume couldn't be rendered inline. Use the buttons below to view or download it.
//                     </p>
//                   </div>
//                   <div className="flex gap-2 flex-wrap justify-center">
//                     <button
//                       onClick={() => {
//                         setViewerState("loading");
//                         setRetryKey((k) => k + 1);
//                       }}
//                       className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 text-xs font-semibold rounded-xl hover:border-gray-300 transition-colors"
//                     >
//                       <RefreshCw className="w-3.5 h-3.5" /> Retry
//                     </button>
//                     <a
//                       href={candidate.resumeUrl}
//                       target="_blank"
//                       rel="noreferrer"
//                       className="flex items-center gap-1.5 px-4 py-2 bg-[#012760] text-white text-xs font-semibold rounded-xl hover:bg-[#023080] transition-colors"
//                     >
//                       <ExternalLink className="w-3.5 h-3.5" /> Open in New Tab
//                     </a>
//                     <a
//                       href={candidate.resumeUrl}
//                       download
//                       className="flex items-center gap-1.5 px-4 py-2 border border-[#012760] text-[#012760] text-xs font-semibold rounded-xl hover:bg-[#012760]/5 transition-colors"
//                     >
//                       <Download className="w-3.5 h-3.5" /> Download
//                     </a>
//                   </div>
//                 </div>
//               )}

//               {/* Google Docs Viewer iframe — never triggers download */}
//               <iframe
//                 key={`${candidate.id}-${retryKey}`}
//                 src={viewerUrl}
//                 className="w-full h-full border-0"
//                 title={`Resume — ${candidate.name}`}
//                 onLoad={() => setViewerState("ready")}
//                 onError={() => setViewerState("error")}
//                 style={{ opacity: viewerState === "ready" ? 1 : 0 }}
//               />
//             </>
//           ) : candidate.resumeUrl ? (
//             // Unsupported inline preview type (.doc/.docx/.txt) — render a
//             // resume-style document generated from the parsed JSON we already
//             // have in the DB, with the original file available as a fallback.
//             <div className="h-full overflow-y-auto bg-gray-100 p-4 md:p-6">
//               <div className="max-w-2xl mx-auto bg-white rounded-sm shadow-md border border-gray-200 p-8 md:p-10">
//                 {/* Header */}
//                 <div className="border-b border-gray-200 pb-4 mb-5">
//                   <h2 className="text-xl font-bold text-gray-800">
//                     {candidate.name && candidate.name !== "Unknown" ? candidate.name : "Unknown Candidate"}
//                   </h2>
//                   <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
//                     {candidate.email && <span>{candidate.email}</span>}
//                     {candidate.phone && <span>{candidate.phone}</span>}
//                     {candidate.experience != null && (
//                       <span className="flex items-center gap-1">
//                         <Briefcase className="w-3 h-3" />
//                         {candidate.experience} yr{candidate.experience !== 1 ? "s" : ""} experience
//                       </span>
//                     )}
//                   </div>
//                 </div>

//                 {/* Skills */}
//                 {(candidate.skills || []).length > 0 && (
//                   <div className="mb-5">
//                     <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Skills</p>
//                     <div className="flex flex-wrap gap-1.5">
//                       {(candidate.skills || []).map((s) => (
//                         <span
//                           key={s}
//                           className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
//                         >
//                           {s}
//                         </span>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {/* Any other parsed fields, shown generically */}
//                 {Object.entries(candidate)
//                   .filter(([key, value]) =>
//                     !["id", "name", "email", "phone", "skills", "experience", "resumeUrl", "createdAt", "_id", "__v"].includes(key) &&
//                     value !== null && value !== undefined && value !== "" &&
//                     !(Array.isArray(value) && value.length === 0)
//                   )
//                   .map(([key, value]) => (
//                     <div key={key} className="mb-4">
//                       <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
//                         {key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}
//                       </p>
//                       {Array.isArray(value) ? (
//                         <div className="flex flex-wrap gap-1.5">
//                           {value.map((v, i) => (
//                             <span
//                               key={i}
//                               className="bg-gray-50 border border-gray-200 text-gray-600 text-[11px] font-medium px-2 py-0.5 rounded-md"
//                             >
//                               {typeof v === "object" ? JSON.stringify(v) : String(v)}
//                             </span>
//                           ))}
//                         </div>
//                       ) : typeof value === "object" ? (
//                         <pre className="text-[11px] text-gray-500 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
//                           {JSON.stringify(value, null, 2)}
//                         </pre>
//                       ) : (
//                         <p className="text-sm text-gray-700">{String(value)}</p>
//                       )}
//                     </div>
//                   ))}

//                 {/* Uploaded date */}
//                 <p className="text-[11px] text-gray-300 mt-6 pt-4 border-t border-gray-100">
//                   Uploaded {new Date(candidate.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
//                 </p>
//               </div>

//               {/* Original file actions */}
//               <div className="max-w-2xl mx-auto mt-3 flex gap-2 justify-end">
//                 <a
//                   href={candidate.resumeUrl}
//                   target="_blank"
//                   rel="noreferrer"
//                   className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-500 text-[11px] font-semibold rounded-lg hover:border-gray-300 transition-colors bg-white"
//                 >
//                   <ExternalLink className="w-3 h-3" /> Open Original File
//                 </a>
//                 <a
//                   href={candidate.resumeUrl}
//                   download
//                   className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-500 text-[11px] font-semibold rounded-lg hover:border-gray-300 transition-colors bg-white"
//                 >
//                   <Download className="w-3 h-3" /> Download Original
//                 </a>
//               </div>
//             </div>
//           ) : (
//             // No resumeUrl — show parsed info fallback
//             <div className="h-full overflow-y-auto p-6">
//               <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center mb-4">
//                 <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
//                   <FileText className="w-5 h-5 text-gray-400" />
//                 </div>
//                 <p className="text-sm font-semibold text-gray-700 mb-1">No file preview available</p>
//                 <p className="text-xs text-gray-400">Resume file URL was not stored.</p>
//               </div>
//               <div className="space-y-4">
//                 <div className="bg-white rounded-xl border border-gray-100 p-4">
//                   <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Parsed Information</p>
//                   <div className="space-y-2">
//                     {[
//                       { label: "Name", value: candidate.name },
//                       { label: "Experience", value: candidate.experience != null ? `${candidate.experience} years` : null },
//                       { label: "Email", value: candidate.email },
//                       { label: "Phone", value: candidate.phone },
//                     ].filter(f => f.value).map(({ label, value }) => (
//                       <div key={label} className="flex items-start gap-3">
//                         <span className="text-xs text-gray-400 w-20 flex-shrink-0">{label}</span>
//                         <span className="text-xs font-medium text-gray-700">{value}</span>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//                 {(candidate.skills || []).length > 0 && (
//                   <div className="bg-white rounded-xl border border-gray-100 p-4">
//                     <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">All Skills</p>
//                     <div className="flex flex-wrap gap-1.5">
//                       {(candidate.skills || []).map((s) => (
//                         <span key={s} className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md">
//                           {s}
//                         </span>
//                       ))}
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       <style>{`
//         @keyframes slideInRight {
//           from { transform: translateX(100%); opacity: 0.6; }
//           to   { transform: translateX(0);    opacity: 1;   }
//         }
//       `}</style>
//     </>
//   );
// };

// export default ResumePreviewPanel;
















// import { useState, useEffect } from "react";
// import {
//   X,
//   AlertCircle,
//   FileText,
//   Loader2,
//   Download,
//   RefreshCw,
//   ExternalLink,
//   Briefcase,
// } from "lucide-react";
// import HighlightText from "@/components/HighlightText";
// const AV_COLORS = [
//   { bg: "#dbeafe", color: "#1d4ed8" },
//   { bg: "#dcfce7", color: "#166534" },
//   { bg: "#ede9fe", color: "#5b21b6" },
//   { bg: "#fff7ed", color: "#c2410c" },
//   { bg: "#fce7f3", color: "#9d174d" },
//   { bg: "#f0fdf4", color: "#14532d" },
// ];

// export const avColor = (name) =>
//   AV_COLORS[(name || "A").charCodeAt(0) % AV_COLORS.length];

// export const initials = (name) =>
//   (name || "?")
//     .split(" ")
//     .map((w) => w[0])
//     .join("")
//     .slice(0, 2)
//     .toUpperCase();

// const GDOCS_VIEWER = (url) =>
//   `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;

// const EXCLUDED_KEYS = [
//   "id", "_id", "__v",
//   "name", "email", "phone", "location", "visaType",
//   "skills", "experience", "resumeUrl", "createdAt",
// ];

// const PinIcon = () => (
//   <svg
//     className="w-3 h-3 text-gray-400 flex-shrink-0"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     viewBox="0 0 24 24"
//   >
//     <path
//       strokeLinecap="round"
//       strokeLinejoin="round"
//       d="M12 2C8.686 2 6 4.686 6 8c0 5.25 6 13 6 13s6-7.75 6-13c0-3.314-2.686-6-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"
//     />
//   </svg>
// );

// const MailIcon = () => (
//   <svg
//     className="w-3 h-3 flex-shrink-0"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     viewBox="0 0 24 24"
//   >
//     <path
//       strokeLinecap="round"
//       strokeLinejoin="round"
//       d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
//     />
//   </svg>
// );

// const PhoneIcon = () => (
//   <svg
//     className="w-3 h-3 flex-shrink-0"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     viewBox="0 0 24 24"
//   >
//     <path
//       strokeLinecap="round"
//       strokeLinejoin="round"
//       d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
//     />
//   </svg>
// );

// const VisaIcon = () => (
//   <svg
//     className="w-3 h-3 flex-shrink-0"
//     fill="none"
//     stroke="currentColor"
//     strokeWidth="2"
//     viewBox="0 0 24 24"
//   >
//     <path
//       strokeLinecap="round"
//       strokeLinejoin="round"
//       d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0"
//     />
//   </svg>
// );

// const ResumePreviewPanel = ({ candidate, onClose}) => {
//   const [viewerState, setViewerState] = useState("loading");
//   const [retryKey, setRetryKey] = useState(0);
//   const av = avColor(candidate?.name);

//   useEffect(() => {
//     setViewerState("loading");
//     setRetryKey((k) => k + 1);
//   }, [candidate?.id]);

//   useEffect(() => {
//     if (viewerState !== "loading") return;
//     const t = setTimeout(() => setViewerState("error"), 30000);
//     return () => clearTimeout(t);
//   }, [viewerState, retryKey]);

//   if (!candidate) return null;

//   const urlLower = (candidate.resumeUrl || "").toLowerCase();
//   const isUnsupported = urlLower.endsWith(".txt");
//   const viewerUrl =
//     candidate.resumeUrl && !isUnsupported
//       ? GDOCS_VIEWER(candidate.resumeUrl)
//       : null;

//   const hasInfo =
//     !!candidate.location ||
//     !!candidate.email ||
//     !!candidate.phone ||
//     (!!candidate.visaType && candidate.visaType !== "Not Specified");

//   return (
//     <>
//       <div
//         className="fixed inset-0 bg-black/30 z-40 md:hidden"
//         onClick={onClose}
//       />

//       <div
//         className="fixed top-0 right-0 h-full z-50 w-full md:w-[520px] lg:w-[600px] bg-white shadow-2xl border-l border-gray-200 flex flex-col"
//         style={{ animation: "slideInRight 0.28s cubic-bezier(0.16,1,0.3,1)" }}
//       >

//         {/* Header */}
//         <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-white flex-shrink-0">
//           <div
//             className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
//             style={{ background: av.bg, color: av.color }}
//           >
//             {initials(candidate.name)}
//           </div>

//           <div className="flex-1 min-w-0">
//             <p className="text-sm font-semibold text-gray-800 truncate">
//               {candidate.name && candidate.name !== "Unknown"
//                 ? candidate.name
//                 : "Unknown Candidate"}
//             </p>
//             <div className="flex items-center gap-3">
//               {candidate.experience != null && (
//                 <span className="text-[11px] text-gray-400 flex items-center gap-1">
//                   <Briefcase className="w-2.5 h-2.5" />
//                   {candidate.experience} yr{candidate.experience !== 1 ? "s" : ""}
//                 </span>
//               )}
//               <span className="text-[11px] text-gray-400">
//                 {new Date(candidate.createdAt).toLocaleDateString("en-IN", {
//                   day: "numeric",
//                   month: "short",
//                   year: "numeric",
//                 })}
//               </span>
//             </div>
//           </div>

//           <div className="flex items-center gap-2 flex-shrink-0">
//             {candidate.resumeUrl && (
//               <a
//                 href={candidate.resumeUrl}
//                 download
//                 title="Download resume"
//                 className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#012760] hover:border-[#012760] transition-colors"
//               >
//                 <Download className="w-3.5 h-3.5" />
//               </a>
//             )}
//             <button
//               onClick={onClose}
//               title="Close preview"
//               className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
//             >
//               <X className="w-3.5 h-3.5" />
//             </button>
//           </div>
//         </div>

//         {/* Info strip */}
//         {hasInfo && (
//           <div className="px-5 py-2.5 border-b border-gray-100 bg-white flex items-center gap-4 flex-wrap flex-shrink-0">
//             {candidate.location && (
//               <span className="flex items-center gap-1 text-[11px] text-gray-500">
//                 <PinIcon />
//                 {candidate.location}
//               </span>
//             )}
//             {candidate.email && (
//               <a
//                 href={`mailto:${candidate.email}`}
//                 className="flex items-center gap-1 text-[11px] text-[#012760] hover:underline"
//               >
//                 <MailIcon />
//                 {candidate.email}
//               </a>
//             )}
//             {candidate.phone && (
//               <a
//                 href={`tel:${candidate.phone}`}
//                 className="flex items-center gap-1 text-[11px] text-[#012760] hover:underline"
//               >
//                 <PhoneIcon />
//                 {candidate.phone}
//               </a>
//             )}
//             {candidate.visaType && candidate.visaType !== "Not Specified" && (
//               <span className="flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-50 border border-purple-100 text-purple-700">
//                 <VisaIcon />
//                 {candidate.visaType}
//               </span>
//             )}
//           </div>
//         )}

//         {/* Skills strip */}
//        {(candidate.skills || []).map(skill => {
//   const isMatch = highlightTerms.some(t => skill.toLowerCase().includes(t));
//   return (
//     <span
//       key={skill}
//       className={`text-[11px] font-medium px-2.5 py-1 rounded-lg border transition-colors
//         ${isMatch
//           ? "bg-yellow-100 border-yellow-300 text-yellow-900 ring-1 ring-yellow-400"
//           : "bg-[#f0f4ff] border-[#dde6ff] text-[#3730a3]"
//         }`}
//     >
//       <HighlightText text={skill} terms={highlightTerms} />
//     </span>
//   );
// })}

//         {/* Viewer area */}
//         <div className="flex-1 relative overflow-hidden bg-gray-100">

//           {viewerUrl ? (
//             <>
//               {viewerState === "loading" && (
//                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 gap-3">
//                   <Loader2 className="w-8 h-8 text-[#012760] animate-spin" />
//                   <p className="text-sm text-gray-400 font-medium">Loading resume…</p>
//                   <p className="text-xs text-gray-300">This may take a few seconds</p>
//                 </div>
//               )}

//               {viewerState === "error" && (
//                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10 px-8 text-center gap-4">
//                   <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center">
//                     <AlertCircle className="w-5 h-5 text-orange-400" />
//                   </div>
//                   <div>
//                     <p className="text-sm font-semibold text-gray-700 mb-1">
//                       Preview unavailable
//                     </p>
//                     <p className="text-xs text-gray-400 leading-relaxed">
//                       The resume could not be rendered inline. Use the buttons below to view or download it.
//                     </p>
//                   </div>
//                   <div className="flex gap-2 flex-wrap justify-center">
//                     <button
//                       onClick={() => {
//                         setViewerState("loading");
//                         setRetryKey((k) => k + 1);
//                       }}
//                       className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 text-xs font-semibold rounded-xl hover:border-gray-300 transition-colors"
//                     >
//                       <RefreshCw className="w-3.5 h-3.5" />
//                       Retry
//                     </button>
//                     <a
//                       href={candidate.resumeUrl}
//                       target="_blank"
//                       rel="noreferrer"
//                       className="flex items-center gap-1.5 px-4 py-2 bg-[#012760] text-white text-xs font-semibold rounded-xl hover:bg-[#023080] transition-colors"
//                     >
//                       <ExternalLink className="w-3.5 h-3.5" />
//                       Open in New Tab
//                     </a>
//                     <a
//                       href={candidate.resumeUrl}
//                       download
//                       className="flex items-center gap-1.5 px-4 py-2 border border-[#012760] text-[#012760] text-xs font-semibold rounded-xl hover:bg-[#012760]/5 transition-colors"
//                     >
//                       <Download className="w-3.5 h-3.5" />
//                       Download
//                     </a>
//                   </div>
//                 </div>
//               )}

//               <iframe
//                 key={`${candidate.id}-${retryKey}`}
//                 src={viewerUrl}
//                 className="w-full h-full border-0"
//                 title={`Resume - ${candidate.name}`}
//                 onLoad={() => setViewerState("ready")}
//                 onError={() => setViewerState("error")}
//                 style={{ opacity: viewerState === "ready" ? 1 : 0 }}
//               />
//             </>

//           ) : candidate.resumeUrl ? (
//             <div className="h-full overflow-y-auto bg-gray-100 p-4 md:p-6">
//               <div className="max-w-2xl mx-auto bg-white rounded-sm shadow-md border border-gray-200 p-8 md:p-10">

//                 <div className="border-b border-gray-200 pb-4 mb-5">
//                   <h2 className="text-xl font-bold text-gray-800">
//                     {candidate.name && candidate.name !== "Unknown"
//                       ? candidate.name
//                       : "Unknown Candidate"}
//                   </h2>
//                   <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
//                     {candidate.email && (
//                       <a
//                         href={`mailto:${candidate.email}`}
//                         className="hover:text-[#012760] hover:underline"
//                       >
//                         {candidate.email}
//                       </a>
//                     )}
//                     {candidate.phone && (
//                       <a
//                         href={`tel:${candidate.phone}`}
//                         className="hover:text-[#012760] hover:underline"
//                       >
//                         {candidate.phone}
//                       </a>
//                     )}
//                     {candidate.location && (
//                       <span>{candidate.location}</span>
//                     )}
//                     {candidate.visaType && candidate.visaType !== "Not Specified" && (
//                       <span className="text-purple-600 font-medium">
//                         {candidate.visaType}
//                       </span>
//                     )}
//                     {candidate.experience != null && (
//                       <span className="flex items-center gap-1">
//                         <Briefcase className="w-3 h-3" />
//                         {candidate.experience} yr{candidate.experience !== 1 ? "s" : ""} experience
//                       </span>
//                     )}
//                   </div>
//                 </div>

//                 {(candidate.skills || []).length > 0 && (
//                   <div className="mb-5">
//                     <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
//                       Skills
//                     </p>
//                     <div className="flex flex-wrap gap-1.5">
//                       {(candidate.skills || []).map((s) => (
//                         <span
//                           key={s}
//                           className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
//                         >
//                           {s}
//                         </span>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {Object.entries(candidate)
//                   .filter(([key, value]) =>
//                     !EXCLUDED_KEYS.includes(key) &&
//                     value !== null &&
//                     value !== undefined &&
//                     value !== "" &&
//                     !(Array.isArray(value) && value.length === 0)
//                   )
//                   .map(([key, value]) => (
//                     <div key={key} className="mb-4">
//                       <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
//                         {key
//                           .replace(/([A-Z])/g, " $1")
//                           .replace(/^./, (c) => c.toUpperCase())}
//                       </p>
//                       {Array.isArray(value) ? (
//                         <div className="flex flex-wrap gap-1.5">
//                           {value.map((v, i) => (
//                             <span
//                               key={i}
//                               className="bg-gray-50 border border-gray-200 text-gray-600 text-[11px] font-medium px-2 py-0.5 rounded-md"
//                             >
//                               {typeof v === "object" ? JSON.stringify(v) : String(v)}
//                             </span>
//                           ))}
//                         </div>
//                       ) : typeof value === "object" ? (
//                         <pre className="text-[11px] text-gray-500 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
//                           {JSON.stringify(value, null, 2)}
//                         </pre>
//                       ) : (
//                         <p className="text-sm text-gray-700">{String(value)}</p>
//                       )}
//                     </div>
//                   ))}

//                 <p className="text-[11px] text-gray-300 mt-6 pt-4 border-t border-gray-100">
//                   Uploaded{" "}
//                   {new Date(candidate.createdAt).toLocaleDateString("en-IN", {
//                     day: "numeric",
//                     month: "short",
//                     year: "numeric",
//                   })}
//                 </p>
//               </div>

//               <div className="max-w-2xl mx-auto mt-3 flex gap-2 justify-end">
//                 <a
//                   href={candidate.resumeUrl}
//                   target="_blank"
//                   rel="noreferrer"
//                   className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-500 text-[11px] font-semibold rounded-lg hover:border-gray-300 transition-colors bg-white"
//                 >
//                   <ExternalLink className="w-3 h-3" />
//                   Open Original File
//                 </a>
//                 <a
//                   href={candidate.resumeUrl}
//                   download
//                   className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-500 text-[11px] font-semibold rounded-lg hover:border-gray-300 transition-colors bg-white"
//                 >
//                   <Download className="w-3 h-3" />
//                   Download Original
//                 </a>
//               </div>
//             </div>

//           ) : (
//             <div className="h-full overflow-y-auto p-6">
//               <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center mb-4">
//                 <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
//                   <FileText className="w-5 h-5 text-gray-400" />
//                 </div>
//                 <p className="text-sm font-semibold text-gray-700 mb-1">
//                   No file preview available
//                 </p>
//                 <p className="text-xs text-gray-400">Resume file URL was not stored.</p>
//               </div>

//               <div className="space-y-4">
//                 <div className="bg-white rounded-xl border border-gray-100 p-4">
//                   <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
//                     Parsed Information
//                   </p>
//                   <div className="space-y-2">
//                     {[
//                       { label: "Name", value: candidate.name },
//                       { label: "Email", value: candidate.email },
//                       { label: "Phone", value: candidate.phone },
//                       { label: "Location", value: candidate.location },
//                       {
//                         label: "Visa Type",
//                         value:
//                           candidate.visaType && candidate.visaType !== "Not Specified"
//                             ? candidate.visaType
//                             : null,
//                       },
//                       {
//                         label: "Experience",
//                         value:
//                           candidate.experience != null
//                             ? `${candidate.experience} years`
//                             : null,
//                       },
//                     ]
//                       .filter((f) => f.value)
//                       .map(({ label, value }) => (
//                         <div key={label} className="flex items-start gap-3">
//                           <span className="text-xs text-gray-400 w-20 flex-shrink-0">
//                             {label}
//                           </span>
//                           <span className="text-xs font-medium text-gray-700">
//                             {value}
//                           </span>
//                         </div>
//                       ))}
//                   </div>
//                 </div>

//                 {(candidate.skills || []).length > 0 && (
//                   <div className="bg-white rounded-xl border border-gray-100 p-4">
//                     <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
//                       All Skills
//                     </p>
//                     <div className="flex flex-wrap gap-1.5">
//                       {(candidate.skills || []).map((s) => (
//                         <span
//                           key={s}
//                           className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
//                         >
//                           {s}
//                         </span>
//                       ))}
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}

//         </div>
//       </div>

//       <style>{`
//         @keyframes slideInRight {
//           from { transform: translateX(100%); opacity: 0.6; }
//           to   { transform: translateX(0);    opacity: 1;   }
//         }
//       `}</style>
//     </>
//   );
// };

// export default ResumePreviewPanel;